import { useEffect, useRef, useState } from 'react';

function carriesFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

/**
 * Lets the user drop image files anywhere on the gallery page.
 *
 * Only drags that carry files from the operating system are handled — in-page drags
 * (text, links, sortable items) have no "Files" type and are left untouched. A drop
 * that a nested drop target already handled (e.g. the PhotoUploadDropzone, which
 * calls preventDefault) is ignored here, so files are never queued twice.
 *
 * This is a pointer convenience on top of the dropzone, which stays the keyboard-
 * and screen-reader-operable way to add photos.
 */
export function useGalleryPageDrop(enabled: boolean, onFiles: (files: File[]) => void): boolean {
  const [isOver, setIsOver] = useState(false);
  const depthRef = useRef(0);
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;

  useEffect(() => {
    if (!enabled) {
      depthRef.current = 0;
      setIsOver(false);
      return;
    }

    const reset = () => {
      depthRef.current = 0;
      setIsOver(false);
    };
    const onDragEnter = (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      depthRef.current += 1;
      setIsOver(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setIsOver(false);
    };
    const onDragOver = (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      // Required for the document to accept the drop instead of the browser opening the file.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDrop = (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      reset();
      if (e.defaultPrevented) return;
      e.preventDefault();
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0) onFilesRef.current(files);
    };

    document.addEventListener('dragenter', onDragEnter);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', onDrop);
    window.addEventListener('dragend', reset);
    window.addEventListener('blur', reset);
    return () => {
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', onDrop);
      window.removeEventListener('dragend', reset);
      window.removeEventListener('blur', reset);
    };
  }, [enabled]);

  return isOver;
}
